import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TaggingOptions, UiKitModule } from '@serviceplan/ui-kit';
import { AssetUIModel, ClientCoreModule, ClientEnvironment, ENVIRONMENT, PROMPTS } from '@thehubz/client/core';
import { UserInfo } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { Subscription } from 'rxjs';
import { customFields } from '../../../customer/customer-asset-fields';
import { AssetCommonService } from '../../../services/asset-common.service';
import { vkbUsageRightsFields } from './../../../customer/vkb/vkb-form-fields';
@Component({
  selector: 'thehubz-custom-asset-fields',
  templateUrl: './custom-asset-fields.component.html',
  styleUrls: ['./custom-asset-fields.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatDividerModule, UiKitModule, ClientCoreModule],
})
export class CustomAssetFieldsComponent implements OnInit, OnDestroy {
  @Input() tabType!: string;
  @Input() assetInfo!: AssetUIModel;

  createCustomFieldForm: UntypedFormGroup;
  customFieldsFormData: AnyType = {
    title: $localize`Custom Fields`,
    description: '',
  };
  assetMetadataSubscription: Subscription | undefined;
  errorFlag = true;
  editFlag = false;
  taggingOptions: TaggingOptions = {
    roles: ['global.library', 'asset.update'],
  };
  taggedUser!: UserInfo[]; // tagged user list
  descriptionText!: string; // sp-editor description text
  brandingKey!: CustomClients;
  formArrayControlLength!: number;
  applyMaxLabelCSSStyle!: string;

  CustomClients = CustomClients;

  textIsRequired = PROMPTS.TEXT.IS_REQUIRED;

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private _formBuilder: UntypedFormBuilder,
    private assetCommonService: AssetCommonService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
    this.createCustomFieldForm = this._formBuilder.group({});
  }

  ngOnInit(): void {
    this.assetMetadataSubscription = this.assetCommonService.metadataEditable.subscribe((data) => {
      this.editFlag = false;
      let formFields: AnyType = [];
      if (this.brandingKey === CustomClients.VKB) {
        if (this.tabType === 'usageRight') {
          formFields = [...vkbUsageRightsFields()];
        }
      } else {
        formFields = customFields.get(this.brandingKey);
      }
      this.applyMaxLabelCSSStyle = this.getMaxLabelCSSStyle(formFields);
      this.customFieldsFormData.formFields = formFields;
      if (data && data != null) {
        this.editFlag = true;
      }
    });
    this.createFormGroup();
  }

  getMaxLabelCSSStyle(formFields: AnyType[]): string {
    let maxLabelLength = 0;
    formFields.forEach((element) => {
      const labelLength = element.fieldLabel ? element.fieldLabel.length : 0;
      if (labelLength > maxLabelLength && !element.hideDetailsView && this.assetInfo.customFields?.[element['formFieldName']]) {
        maxLabelLength = element.fieldLabel.length;
      }
    });
    if (maxLabelLength >= 10 && maxLabelLength <= 16) {
      return 'w-2/5';
    }

    if (maxLabelLength >= 10 && maxLabelLength <= 26) {
      return 'w-3/5';
    }

    if (maxLabelLength > 26) {
      return 'w-[70%]';
    }

    return 'w-2/5';
  }

  /**
   * This function is used to create dynamic form group based on form fields data
   */
  createFormGroup() {
    this.createCustomFieldForm = new UntypedFormGroup({});
    this.customFieldsFormData['formFields']?.forEach((element: AnyType) => {
      const fieldObject: AnyType = element;
      if (
        fieldObject['formFieldName'] &&
        fieldObject['formFieldName'] !== '' &&
        (!Object.prototype.hasOwnProperty.call(fieldObject, 'editView') || fieldObject['editView']) &&
        fieldObject['fieldGroup'] === this.tabType &&
        (!fieldObject['assetType'] || (fieldObject['assetType'] && fieldObject['assetType'].includes(this.assetInfo.assetType)))
      ) {
        const fieldName = element['formFieldName'];
        const control: UntypedFormControl = this.createFormFieldControl(fieldObject);
        if (fieldObject['disable']) {
          control.disable();
        }
        if (fieldObject['fieldType'] == 'formArray') {
          const control: UntypedFormArray = this.buildFormArray(fieldObject.formFieldGroup, fieldObject.formFieldName);
          this.createCustomFieldForm.addControl(fieldName, control);
          return;
        }
        this.createCustomFieldForm.addControl(fieldName, control);
      }
    });
    this.createCustomFieldForm.valueChanges.subscribe(() => {
      this.errorFlag = true;
      if (this.createCustomFieldForm.status == 'VALID') {
        this.errorFlag = false;
      }
    });
  }

  /**
   * Build form array
   * @param formArray
   * @returns
   */
  buildFormArray(formArray: AnyType, formFieldName: string): UntypedFormArray {
    const control = new UntypedFormArray([]);
    if (this.assetInfo.customFields?.[formFieldName] && this.assetInfo.customFields[formFieldName].length) {
      this.assetInfo.customFields[formFieldName].forEach((customField: AnyType) => {
        const groupControl = this.buildFormGroup(formArray, customField);
        control.push(groupControl);
      });
    } else {
      const groupControl = this.buildFormGroup(formArray);
      control.push(groupControl);
    }
    return control;
  }

  buildFormGroup(formArray: AnyType, customFieldValue?: AnyType): UntypedFormGroup {
    const groupControl: UntypedFormGroup = new UntypedFormGroup({});
    formArray.forEach((formField: AnyType) => {
      const fieldName = formField['formFieldName'];
      const control: UntypedFormControl = this.createFormArrayFormFieldControl(formField, customFieldValue?.[fieldName]);
      if (formField['disable']) {
        control.disable();
      }
      groupControl.addControl(fieldName, control);
    });
    return groupControl;
  }

  /**
   * This function is used for formArray. Mainly for the milestone. It will return the form control based on field name
   * @param fieldArrayName
   * @param formGroupName
   * @returns
   */
  getControls(fieldArrayName: AnyType) {
    if (this.createCustomFieldForm.controls[fieldArrayName]) {
      const formArray = this.createCustomFieldForm.controls[fieldArrayName] as UntypedFormArray;
      this.formArrayControlLength = formArray.controls.length;
      return formArray.controls;
    }
    return [];
  }

  /**
   * It will validate every form fields based on provided regex string
   * @param regexString
   * @returns
   */
  validateFormat(regexString: string) {
    const regex = new RegExp(regexString);
    return (input: UntypedFormControl) => {
      if (input.value) {
        return regex.test(input.value) ? null : { validformat: true };
      }
      return null;
    };
  }

  /**
   * It will create a dynamic form fields based with different validation.
   *
   * @param fieldObject
   * @returns
   */
  createFormFieldControl(fieldObject: AnyType) {
    const fieldValidation = this.addFormFieldValidation(fieldObject);
    let fieldPAtchValue = '';
    if (this.assetInfo && this.assetInfo.customFields) {
      fieldPAtchValue = this.assetInfo.customFields[fieldObject['formFieldName']];
      // show or hide channel and type based on the patch value
      ['channel', 'type'].forEach((item) => {
        if (fieldObject['formFieldName'] === item) this.assetCommonService.findFormFields(item, !!fieldPAtchValue, this.customFieldsFormData);
      });
    }
    return new UntypedFormControl(fieldPAtchValue, fieldValidation);
  }

  /**
   * It will create a dynamic form fields based with different validation.
   *
   * @param fieldObject
   * @returns
   */
  createFormArrayFormFieldControl(fieldObject: AnyType, customFieldValue?: AnyType) {
    const fieldValidation = this.addFormFieldValidation(fieldObject);
    let fieldPAtchValue = '';
    if (this.assetInfo && this.assetInfo.customFields) {
      fieldPAtchValue = customFieldValue;
    }
    return new UntypedFormControl(fieldPAtchValue, fieldValidation);
  }

  addFormFieldValidation(fieldObject: AnyType) {
    const fieldValidation: AnyType = [];
    if (fieldObject['mandatory']) {
      fieldValidation.push(Validators.required);
    }
    if (fieldObject['maxlength']) {
      fieldValidation.push(Validators.maxLength(fieldObject['maxlength']));
    }
    if (fieldObject['minlength']) {
      fieldValidation.push(Validators.minLength(fieldObject['minlength']));
    }
    if (fieldObject['regex']) {
      fieldValidation.push(this.validateFormat(fieldObject['regex']));
    }
    return fieldValidation;
  }

  /**
   * store tagged user list when user tagged get at sp-editor
   * @param userList
   */
  taggedUserList(userList: UserInfo[]) {
    this.taggedUser = userList;
  }

  /**
   * store description text from sp-editor change
   * @param descriptionText
   */
  editorDescriptionText(descriptionText: string) {
    this.descriptionText = descriptionText;
  }

  onValueChange(formFieldName: string) {
    this.assetCommonService.updateArvalFormData(formFieldName, this.customFieldsFormData);
  }

  ngOnDestroy(): void {
    if (this.assetMetadataSubscription) this.assetMetadataSubscription.unsubscribe();
  }
}
